import { useCallback, useMemo } from 'react';

import { OrganizationUpdateForm } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { organizationService } from '@organization/service/organizaion/organizationService';
import { translate } from '@chaika/i18n/helper';

import {
    CreationMenu,
    CreationMenuButtonsParams,
    CreationMenuTitleModifiers,
} from '@components/Table/CreationMenu/CreationMenu';
import { TableField, TableFieldType } from '@components/Table/CreationMenu';

import { OrganizationRow } from './useColumns';

interface Data {
    openEditMenu(record: OrganizationRow): void;
}

export function useCreationMenu(): Data {
    const { app } = store;

    const onCloseMenu = useCallback((): void => {
        app.resetRightMenu();
    }, [app]);

    const defaultButtonsParams: CreationMenuButtonsParams = useMemo(
        () => ({
            cancelButtonOptions: {
                dataTest: '',
                onClick: onCloseMenu,
            },
        }),
        [onCloseMenu],
    );

    const createMenuItemFields = useCallback(
        (record: OrganizationRow): TableField[] => [
            {
                label: 'Store ID',
                fieldName: 'storeId',
                value: record.storeId,
                dataTest: 'storeId',
                type: TableFieldType.Input,
            },
        ],
        [],
    );

    const handleUpdate = useCallback(
        async (id: string, params: OrganizationUpdateForm): Promise<void> => {
            app.isOnUpdate = true;
            await organizationService.update(id, { storeId: params.storeId });
            onCloseMenu();
            app.isOnUpdate = false;
        },
        [app, onCloseMenu],
    );

    const openEditMenu = useCallback(
        (record: OrganizationRow): void => {
            const fields = createMenuItemFields(record);

            const buttonsParams: CreationMenuButtonsParams = {
                ...defaultButtonsParams,
                saveButtonOptions: {
                    dataTest: '',
                    onClick: (params: OrganizationUpdateForm) => handleUpdate(record.id, params),
                },
            };

            app.setRightMenu({
                children: (
                    <CreationMenu
                        title={`${translate('edit')} «${record.name}»`}
                        titleModifiers={[CreationMenuTitleModifiers.WithTopOffset]}
                        fields={fields}
                        buttonsParams={buttonsParams}
                    />
                ),
                onClickOutside: onCloseMenu,
            });
        },
        [app, createMenuItemFields, defaultButtonsParams, handleUpdate, onCloseMenu],
    );

    return { openEditMenu };
}
